import { createSlice } from '@reduxjs/toolkit';

// Define FulfillmentGroup statuses for filtering (matching backend)
const FG_ONGOING_STATUSES = [
  'processing',
  'coordinating_pickup',
  'coordinating_return',
  'ready_for_pickup',
  'shipped_to_customer',
  'delivery_in_progress',
  'awaiting_return_shipment',
  'awaiting_return_dropoff',
  'return_in_transit_to_merchant',
  'return_processing_by_merchant',
  'renting_in_progress'
];

const FG_COMPLETED_STATUSES = [
  'pickup_completed_by_customer',
  'delivery_completed_to_customer',
  'return_completed_for_group'
];

const FG_CANCELED_STATUS = 'canceled';

// Helper function to determine order display status based on fulfillment group statuses
const getOrderDisplayStatus = (fulfillmentGroups) => {
  if (!fulfillmentGroups || fulfillmentGroups.length === 0) {
    return 'unknown';
  }

  const fgStatuses = fulfillmentGroups.map(fg => fg.status);
  
  // If all FGs are completed, order is completed
  if (fgStatuses.every(status => FG_COMPLETED_STATUSES.includes(status))) {
    return 'completed';
  }
  
  // If all FGs are canceled, order is canceled
  if (fgStatuses.every(status => status === FG_CANCELED_STATUS)) {
    return 'canceled';
  }
  
  // If any FG is ongoing, order is ongoing
  if (fgStatuses.some(status => FG_ONGOING_STATUSES.includes(status))) {
    return 'ongoing';
  }
  
  // Default to ongoing for other statuses (processing, etc.)
  return 'ongoing';
};

// Filter orders based on fulfillment group status instead of order status
const filterOrdersByFGStatus = (orders, statusFilter) => {
  if (!statusFilter || statusFilter === 'all') {
    return orders;
  }

  return orders.filter(order => {
    const displayStatus = getOrderDisplayStatus(order.fulfillmentGroups);
    return displayStatus === statusFilter;
  });
};

const initialState = {
  // Transaction filtering state
  roleTab: 'all', // all, buying, selling, renting, lending
  statusTab: 'all', // all, ongoing, completed, canceled
  
  // Data state
  myOrders: [],
  merchantOrders: [],
  allOrders: [], // Combined orders for display
  
  // Pagination state
  myOrdersPage: 0,
  merchantOrdersPage: 0,
  hasMoreMyOrders: true,
  hasMoreMerchantOrders: true,
  
  // Loading state
  loading: false,
  error: null,
  isInitialized: false,
  
  // Legacy state (keep for backward compatibility)
  orders: [],
  currentOrder: null,
  transferStatus: null,
  transferDetails: null,
  
  // Add loading states
  transactionLoading: false,
  myOrdersLoading: false,
  merchantOrdersLoading: false,
};

// Parse URL parameters for transactions
const parseTransactionUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const roleTab = params.get('role') || 'all';
  const statusTab = params.get('status') || 'all';
  return { roleTab, statusTab };
};

// Update URL with transaction parameters (helper function for components)
export const updateTransactionUrl = (roleTab, statusTab) => {
  const params = new URLSearchParams();
  if (roleTab && roleTab !== 'all') params.set('role', roleTab);
  if (statusTab && statusTab !== 'all') params.set('status', statusTab);
  
  const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
  window.history.pushState({ path: newUrl }, '', newUrl);
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // New transaction filtering actions
    setTransactionFilters: (state, action) => {
      const { roleTab, statusTab, resetPagination } = action.payload;
      
      // Set loading state immediately
      state.transactionLoading = true;
      state.myOrdersLoading = true;
      state.merchantOrdersLoading = true;
      
      if (roleTab !== undefined) state.roleTab = roleTab;
      if (statusTab !== undefined) state.statusTab = statusTab;
      
      if (resetPagination) {
        state.myOrdersPage = 0;
        state.merchantOrdersPage = 0;
        state.hasMoreMyOrders = true;
        state.hasMoreMerchantOrders = true;
        state.myOrders = [];
        state.merchantOrders = [];
        state.allOrders = [];
      }
    },
    
    setMyOrders: (state, action) => {
      const { data, page, hasMore } = action.payload;
      if (page === 0) {
        state.myOrders = data;
      } else {
        state.myOrders = [...state.myOrders, ...data];
      }
      state.hasMoreMyOrders = hasMore;
      state.myOrdersPage = page;
      
      // Clear loading state when data is received
      state.myOrdersLoading = false;
      
      // If this is the only active query, clear transaction loading
      if (!state.merchantOrdersLoading) {
        state.transactionLoading = false;
      }
    },
    
    setMerchantOrders: (state, action) => {
      const { data, page, hasMore } = action.payload;
      if (page === 0) {
        state.merchantOrders = data;
      } else {
        state.merchantOrders = [...state.merchantOrders, ...data];
      }
      state.hasMoreMerchantOrders = hasMore;
      state.merchantOrdersPage = page;
      
      // Clear loading state when data is received
      state.merchantOrdersLoading = false;
      
      // If this is the only active query, clear transaction loading
      if (!state.myOrdersLoading) {
        state.transactionLoading = false;
      }
    },
    
    combineOrders: (state) => {
      // Combine and sort orders based on current tab
      let combinedOrders = [];
      
      if (state.roleTab === 'all') {
        // Show both my orders and merchant orders
        const myOrdersWithSource = state.myOrders.map(o => ({ ...o, source: 'my' }));
        const merchantOrdersWithSource = state.merchantOrders.map(o => ({ ...o, source: 'merchant' }));
        combinedOrders = [...myOrdersWithSource, ...merchantOrdersWithSource];
        combinedOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else if (['buying', 'renting'].includes(state.roleTab)) {
        combinedOrders = state.myOrders.map(o => ({ ...o, source: 'my' }));
      } else if (['selling', 'lending'].includes(state.roleTab)) {
        combinedOrders = state.merchantOrders.map(o => ({ ...o, source: 'merchant' }));
      }
      
      // Apply FG status filtering as backup (backend should handle this but this ensures consistency)
      combinedOrders = filterOrdersByFGStatus(combinedOrders, state.statusTab);
      
      // Add display status to each order for debugging
      combinedOrders = combinedOrders.map(order => ({
        ...order,
        displayStatus: getOrderDisplayStatus(order.fulfillmentGroups)
      }));
      
      state.allOrders = combinedOrders;
    },
    
    loadMoreMyOrders: (state) => {
      state.myOrdersPage += 1;
    },
    
    loadMoreMerchantOrders: (state) => {
      state.merchantOrdersPage += 1;
    },
    
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    resetTransactionState: (state) => {
      state.myOrders = [];
      state.merchantOrders = [];
      state.allOrders = [];
      state.myOrdersPage = 0;
      state.merchantOrdersPage = 0;
      state.hasMoreMyOrders = true;
      state.hasMoreMerchantOrders = true;
      state.loading = false;
      state.error = null;
    },
    
    // Legacy actions (keep for backward compatibility)
    setOrders: (state, action) => {
      state.orders = action.payload;
    },
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    },
    setTransferStatus: (state, action) => {
      state.transferStatus = action.payload;
    },
    setTransferDetails: (state, action) => {
      state.transferDetails = action.payload;
    },
    
    setMyOrdersLoading: (state, action) => {
      state.myOrdersLoading = action.payload;
    },
    
    setMerchantOrdersLoading: (state, action) => {
      state.merchantOrdersLoading = action.payload;
    },
    
    setTransactionLoading: (state, action) => {
      state.transactionLoading = action.payload;
    },
  },
});

export const {
  // New transaction actions
  setTransactionFilters,
  setMyOrders,
  setMerchantOrders,
  combineOrders,
  loadMoreMyOrders,
  loadMoreMerchantOrders,
  setLoading,
  setError,
  clearError,
  resetTransactionState,
  
  // Legacy actions
  setOrders,
  setCurrentOrder,
  clearCurrentOrder,
  setTransferStatus,
  setTransferDetails,
  
  // New loading actions
  setMyOrdersLoading,
  setMerchantOrdersLoading,
  setTransactionLoading,
} = orderSlice.actions;

export default orderSlice.reducer; 